import Vue from 'vue'
import axios from 'axios'
import Router from 'vue-router'
import store from '../store'

import LayoutExternal from '../layouts/external.vue'
import LayoutInternal from '../layouts/internal.vue'
import NoSideBar from '../views/sidebars/SidebarNone.vue'

import {AUTH_ACCESS_TEAM_WITH_CALLBACK_URL, AUTH_SET_REDIRECT_PATH} from '../store/mutations'
import {INVITE_LOOKUP} from '../store/modules/auth/invite'
import {REFERRAL_LOOKUP} from '../store/modules/auth/referral'
import {SETTINGS_WORKFLOWS} from '../views/settings/settings-tabs'
import {getDocumentSectionsRoutes} from './modules/documents'
import DocumentsSearch from '@/components/elasticsearch/DocumentsSearch'

const Login = () => import(/* webpackChunkName: "login-page" */ '../views/auth/Login')
const AutoLoginPage = () => import(/* webpackChunkName: "auto-login-page" */ '../views/auth/AutoLogin')
const Register = () => import(/* webpackChunkName: "register-page" */ '../views/auth/Register')
const PasswordReset = () => import(/* webpackChunkName: "reset-password" */ '../views/auth/PasswordReset')
const PasswordForgot = () => import(/* webpackChunkName: "forgot-password" */ '../views/auth/PasswordForgot')
const VerifyEmailPage = () => import(/* webpackChunkName: "verify-email" */ '../views/auth/Verify')

const Dashboard = () => import(/* webpackChunkName: "dashboard-page" */ '../views/dashboard/Dashboard')
const Inbox = () => import(/* webpackChunkName: "inbox" */ '../views/inbox/InboxTab')
const Notification = () => import(/* webpackChunkName: "notifications-page" */ '../views/notifications/Notifications')
const Settings = () => import(/* webpackChunkName: "settings-page" */ '../views/settings/Settings')
const Reports = () => import(/* webpackChunkName: "reports" */ '../views/reports-v2/Reports.vue')
const DtView = () => import(/* webpackChunkName: "documents-list" */ '../views/documents/types/views/DtView')

const SidebarPortal = () => import(/* webpackChunkName: "sidebar-portal" */ '../views/sidebars/SidebarPortal')
const SidebarDocuments = () => import(/* webpackChunkName: "sidebar-documents" */ '../views/sidebars/documents/SidebarDocuments')

const beforeExternalPageEnter = (to, from, next) => {
    if (store.getters.isAuthenticated) {
        // To create a company - the second step in the registration process (is this still relevant?)
        // or accept the invitation
        if (to.name === 'register') {
            next()
        } else if (store.getters.activeTeam) {
            next({name: 'dashboard', params: {team: store.getters.activeTeamId}})
        } else {
            next({name: 'register'})
        }
    } else {
        next()
    }
}

const beforeInternalPageEnter = (to, from, next) => {
    if (store.getters.isAuthenticated) {
        if (store.getters.activeTeam) {
            axios.defaults.headers.common['x-team'] = store.getters.activeTeamId
            if (store.getters.authRedirectPath) {
                const path = store.getters.authRedirectPath
                store.commit(AUTH_SET_REDIRECT_PATH, null)
                next({path})
            } else {
                next()
            }
        } else {
            next({name: 'register'})
        }
    } else {
        store.commit(AUTH_SET_REDIRECT_PATH, to.path)
        next({name: 'login'})
    }
}

const unknownPageRedirect = () => {
    if (store.getters.isAuthenticated) {
        if (store.getters.activeTeam) {
            return {name: 'dashboard', params: {team: store.getters.activeTeamId}}
        } else {
            return {name: 'register'}
        }
    } else {
        return {name: 'login'}
    }
}

const beforeRootPageEnter = async(to, from, next) => {
    if (window.location.href &&
        window.location.href.includes('?callback') &&
        window.location.href.split('?callback=')[1]) {
        store.dispatch(AUTH_ACCESS_TEAM_WITH_CALLBACK_URL, window.location.href.split('?callback=')[1])
            .then(() => {
                next({name: 'dashboard', params: {team: store.getters.activeTeamId}})
            })
            .catch(() => {
                next({name: 'login'})
            })
    } else {
        next(unknownPageRedirect())
    }
}

Vue.use(Router)

export const router = new Router({
    mode: 'history',
    routes: [
        {
            path: '/',
            component: LayoutExternal,
            children: [
                {
                    path: '/',
                    beforeEnter: beforeRootPageEnter
                },
                {
                    path: '/login',
                    name: 'login',
                    props: true,
                    component: Login,
                    beforeEnter: beforeExternalPageEnter,
                    meta: {
                        title: 'Login'
                    }
                },
                {
                    path: '/register/:type?',
                    name: 'register',
                    props: true,
                    component: Register,
                    beforeEnter: beforeExternalPageEnter,
                    meta: {
                        title: 'Register'
                    }
                },
                {
                    path: '/reset',
                    name: 'forgot',
                    props: true,
                    component: PasswordForgot,
                    beforeEnter: beforeExternalPageEnter,
                    meta: {
                        title: 'Reset password'
                    }
                },
                {
                    path: '/reset/:uuid/:expires/:sig',
                    name: 'reset',
                    props: true,
                    component: PasswordReset,
                    beforeEnter: beforeExternalPageEnter,
                    meta: {
                        title: 'Reset password'
                    }
                },
                {
                    path: '/access/:uuid/:expires/:sig',
                    name: 'access',
                    props: true,
                    component: AutoLoginPage,
                    meta: {
                        title: 'Logging in'
                    }
                },
                {
                    path: '/verify/:type',
                    props: true,
                    component: VerifyEmailPage,
                    meta: {
                        title: 'Verify email'
                    }
                }
            ]
        },
        {
            path: '/app',
            component: LayoutInternal,
            beforeEnter: beforeInternalPageEnter,
            children: [
                {
                    path: '/app/:team',
                    name: 'dashboard',
                    components: {default: Dashboard, sidebar: NoSideBar},
                    props: {default: true, sidebar: false}
                }, {
                    path: '/app/:team/inbox',
                    name: 'inbox',
                    components: {default: Inbox, sidebar: NoSideBar},
                    props: {default: true, sidebar: false}
                },
                ...getDocumentSectionsRoutes({
                    defaultComponent: DtView,
                    sidebarComponent: SidebarDocuments
                }),
                {
                    path: '/app/:team/notifications',
                    name: 'notifications',
                    components: {default: Notification, sidebar: NoSideBar},
                    props: {default: true, sidebar: false}
                },
                {
                    path: '/app/:team/settings/:page',
                    name: 'settings',
                    components: {default: Settings, sidebar: SidebarPortal},
                    props: {default: true, sidebar: false},
                    children: [{
                        name: 'workflow.view',
                        path: `/app/:team/settings/${SETTINGS_WORKFLOWS}/:uuid`
                    }]
                },
                {
                    path: '/app/:team/reports/:name?',
                    name: 'reports',
                    components: {default: Reports, sidebar: SidebarPortal},
                    props: {default: true, sidebar: false}
                },
                {
                    path: '/app/:team/search',
                    name: 'search',
                    components: {default: DocumentsSearch, sidebar: NoSideBar},
                    props: {default: true, sidebar: false}
                }
            ]
        },
        {
            path: '/invite/:code',
            beforeEnter: (to, from, next) => {
                store.dispatch(INVITE_LOOKUP, to.params.code)
                    .then(() => {
                        next({name: 'register'})
                    })
                    .catch(err => {
                        if (err.response.status === 404) {
                            next({name: 'register', params: {isExpiredInvite: true}})
                        } else {
                            next({name: 'register'}) // handle another error codes?
                        }
                    })
            }
        },
        {
            path: '/referral/:code',
            beforeEnter: (to, from, next) => {
                store.dispatch(REFERRAL_LOOKUP, to.params.code)
                    .then(() => {
                        next({name: 'register'})
                    })
                    .catch(err => {
                        if (err.response.status === 404) {
                            next({name: 'register', params: {isExpiredReferral: true}})
                        } else {
                            next({name: 'register'}) // handle another error codes?
                        }
                    })
            }
        },
        {
            path: '*',
            redirect: unknownPageRedirect
        }
    ]
})

router.beforeEach((to, from, next) => {
    store.commit('extend_route/setPrevRoute', {
        name: from.name,
        path: from.path,
        query: from.query
    })
    store.commit('extend_route/setCurrentRoute', {
        name: to.name,
        path: to.path
    })
    next(true)
})

// router.onError(error => {
//     if (error.name === 'ChunkLoadError') {
//         window.location.reload()
//     }
// })

export default router
